
import { Calendar, ChevronDown, Search, X } from 'react-feather';
import { ChangeEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uniqBy from 'lodash/uniqBy';

import { GetEventPickerEventsQuery as GetEventPickerEvents } from '__generated__/graphql';

import GetEventPickerEventsQuery from './GetEventPickerEventsQuery';
import UI from '../../common/UI';
import useFocusIndex from '../../common/useFocusIndex';
import useLocale from '../../common/useLocale';
import useOnClickOutside from '../../common/useOnClickOutside';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

export type Event = GetEventPickerEvents['project']['events']['data'][0];

export interface EventPickerProps {
  eventId?: string;
  onChange?: (event: Event) => void;
  preselect?: (events: Event[]) => Event | null | undefined;
  renderActive?: (event?: Event) => ReactNode;
  nullable?: boolean;
  buttonId?: string;
}

const EventPicker = ({
  eventId, onChange, preselect, renderActive, nullable = true, buttonId,
}: EventPickerProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { events, loadMore, total, loading } = useEvents({ search, skip: !preselect && !isOpen && !eventId });
  const [event, setEvent] = useState(events.filter(({ id }) => id === eventId)[0]);
  const searchRef = useRef<HTMLInputElement>();

  const reset = () => {
    setSearch('');

    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  useEffect(() => {
    // If an eventId is set by the parent, then fetch events until we come across that event.
    if (eventId && !loading) {
      const activeEvent = events.filter((event) => event.id === eventId)[0];

      if (activeEvent && !event) {
        setEvent(activeEvent);
      } else if (!activeEvent && events.length < total) {
        loadMore();
      }
    }
  }, [eventId, loading, event, events, total, loadMore]);

  useEffect(() => {
    // If the eventId prop gets unset by the parent, update the state of the EventPicker accordingly.
    if (!eventId && event) {
      setEvent(null);
      reset();
    }
  }, [eventId, event]);

  /** Index 0 is reserved for 'all events' */
  const activeIndex = events.findIndex(({ id }) => id === event?.id) + (nullable ? 1 : 0);
  const maxIndex = events.length - (nullable ? 0 : 1);

  /* eslint-disable @typescript-eslint/no-use-before-define */
  const [focusIndex, setFocusIndex, listRef] = useFocusIndex({
    initialIndex: Math.max(0, activeIndex),
    maxIndex,
    active: isOpen,
    onSelect: (index) => selectEvent(events[index - (nullable ? 1 : 0)]),
    onCancel: () => close(),
  });
  /* eslint-enable @typescript-eslint/no-use-before-define */

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  useEffect(() => {
    const min = search ? Math.min(1, maxIndex) : 0;
    setFocusIndex(Math.max(min, activeIndex));
  }, [search, maxIndex, activeIndex, isOpen, setFocusIndex]);

  const close = () => {
    setIsOpen(false);
  };

  const open = () => {
    setIsOpen(true);
  };

  const selectEvent = useCallback((event?: Event) => {
    setEvent(event);
    onChange?.(event);
    reset();
    close();
  }, [onChange]);

  const toggleDropdown = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    }
  }, [isOpen]);

  const containerRef = useRef<HTMLDivElement>();

  useOnClickOutside(containerRef, () => {
    close();
  });

  const preselectedRef = useRef(false);

  // Run the preselect callback once if present and no eventId is set
  useEffect(() => {
    if (!preselectedRef.current && preselect) {
      if (events.length > 0 && events.length === total) {
        if (!eventId) {
          const event = preselect(events);

          if (event) {
            selectEvent(event);
          }
        }

        preselectedRef.current = true;
      }
    }
  }, [events, total, preselect, eventId, selectEvent]);

  return (
    <UI.PickerContainer ref={containerRef}>
      <UI.PickerDropdown open={isOpen}>
        <button type="button" onClick={() => toggleDropdown()} id={buttonId}>
          <UI.Span sc={{ muted: eventId && !event }}>
            <UI.Truncate>
              {renderActive?.(event)}
              {!renderActive && event && (
                <>
                  <UI.Icon>
                    <Calendar />
                  </UI.Icon>
                  {' '}
                  {event.title}
                </>
              )}
              {!renderActive && !event && (
                <UI.Span sc={{ color: 'gray.400' }}>{t('choose_event')}</UI.Span>
              )}
            </UI.Truncate>
            {' '}
            <UI.Icon><ChevronDown /></UI.Icon>
          </UI.Span>
        </button>
        {isOpen && (
          <UI.Div>
            <UI.InputGroup sc={{ mb: 2 }}>
              <UI.InputWrapper>
                <UI.Span>
                  <UI.Icon>
                    <Search />
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  type="text"
                  value={search}
                  onChange={handleSearch}
                  placeholder={t('search')}
                  ref={searchRef}
                  sc={{ px: 5 }}
                />
                {search && (
                  <UI.Span>
                    <UI.A onClick={() => reset()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <X />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                )}
              </UI.InputWrapper>
            </UI.InputGroup>
            <UI.Div id="EventPickerList" ref={listRef}>
              {nullable && (
                <>
                  <UI.PickerItem
                    type="button"
                    id="EventPickerList_0"
                    onClick={() => selectEvent(null)}
                    onFocus={() => setFocusIndex(0)}
                    onMouseEnter={() => setFocusIndex(0)}
                    active={activeIndex === 0}
                    focus={focusIndex === 0}
                    tabIndex={-1}
                  >
                    {t('all_events')}
                  </UI.PickerItem>
                  <UI.HR />
                </>
              )}
              {events.map((event, index) => (
                <UI.PickerItem
                  type="button"
                  id={`EventPickerList_${index + (nullable ? 1 : 0)}`}
                  onClick={() => selectEvent(event)}
                  onFocus={() => setFocusIndex(index + (nullable ? 1 : 0))}
                  onMouseEnter={() => setFocusIndex(index + (nullable ? 1 : 0))}
                  active={activeIndex === index + (nullable ? 1 : 0)}
                  focus={focusIndex === index + (nullable ? 1 : 0)}
                  tabIndex={-1}
                  key={event.id}
                >
                  <UI.Span sc={{ fontWeight: 500, block: true }}>
                    {event.title}
                  </UI.Span>
                  <UI.Span sc={{ muted: true }}>
                    {formatDate(parseDate(event.start_date, { timezone: 'UTC' }), { format: 'display_date' })}
                  </UI.Span>
                </UI.PickerItem>
              ))}
              {total > events.length && (
                <>
                  <UI.HR />
                  {!loading && (
                    <UI.PickerItem
                      type="button"
                      id={`EventPickerList_${events.length + 1}`}
                      onClick={() => loadMore()}
                      onFocus={() => setFocusIndex(events.length + 1)}
                      onMouseEnter={() => setFocusIndex(events.length + 1)}
                      active={activeIndex === events.length + 1}
                      focus={focusIndex === events.length + 1}
                      tabIndex={-1}
                    >
                      {t('show_more')}
                    </UI.PickerItem>
                  )}
                  {loading && <UI.Loader />}
                </>
              )}
              {events.length === 0 && loading && <UI.Loader />}
              {!loading && events.length === 0 && (
                <UI.Span sc={{ muted: true }} style={{ padding: '0 10px' }}>
                  {t('no_results')}
                </UI.Span>
              )}
            </UI.Div>
          </UI.Div>
        )}
      </UI.PickerDropdown>
    </UI.PickerContainer>
  );
};

interface UseEventsOptions {
  search?: string;
  page?: number;
  perPage?: number;
  skip?: boolean;
}

/**
 * Fetches a fixed number of events and appends them to the list.
 */
export const useEvents = ({ search = '', perPage = 100, skip }: UseEventsOptions = {}) => {
  const project = useProject();

  const [events, setEvents] = useState<Event[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(events.length);
  const [called, setCalled] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const { loading } = useQuery(
    GetEventPickerEventsQuery,
    {
      variables: {
        project_id: project.id,
        page,
        per_page: perPage,
        search,
      },
      skip,
      onCompleted: (data) => {
        setEvents((events) => (
          data.project.events.current_page === 1
            ? data.project.events.data
            : uniqBy([...events, ...data.project.events.data], 'id')
        ));

        setPage(data.project.events.current_page);
        setTotal(data.project.events.total);
        setCalled(true);
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const loadMore = () => setPage((page) => page + 1);

  return {
    events,
    loadMore,
    loading,
    total,
    called,
  };
};

export default EventPicker;
